import { memo } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import type { DuelSide, Fixture } from '@h2h/shared';
import { ChevronDown, ChevronUp, GripVertical } from '../../ui/icons';
import { SPRING_REORDER } from '../../../lib/motion';
import { parseRiderName } from '../../../lib/riderDisplay';
import { RiderAvatar } from './RiderAvatar';

export type LadderRungData = {
  duelId: string;
  pick: DuelSide;
  fixture: Fixture;
};

function riderLine(name: string, team?: string | null) {
  const { surname, firstName } = parseRiderName(name);
  return (
    <div className="truncate text-[13.5px] tracking-[-0.01em] text-ink">
      <span className="font-extrabold">{surname.toUpperCase()}</span>
      {firstName ? <span className="font-medium text-[#6E6E73]"> {firstName}</span> : null}
      {team ? <span className="text-[11.5px] font-semibold text-ink3"> · {team}</span> : null}
    </div>
  );
}

function opponentLine(
  name: string,
  nationality: string | null | undefined,
  team?: string | null
) {
  const { surname, firstName } = parseRiderName(name);
  return (
    <div className="mt-[3px] flex min-w-0 items-center gap-1.5">
      <RiderAvatar name={name} nationality={nationality} size="xs" />
      <span className="truncate text-[11.5px] tracking-[-0.01em] text-ink3">
        <span className="font-bold">{surname.toUpperCase()}</span>
        {firstName ? <span className="font-medium"> {firstName}</span> : null}
        {team ? <span className="font-medium"> · {team}</span> : null}
      </span>
    </div>
  );
}

type StakeLadderRungProps = {
  id: string;
  data: LadderRungData;
  rank: number;
  total: number;
  disabled?: boolean;
  onMove: (from: number, to: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
};

function StakeLadderRungBase({
  id,
  data,
  rank,
  total,
  disabled,
  onMove,
  onDragStart,
  onDragEnd
}: StakeLadderRungProps) {
  const controls = useDragControls();
  const winnerSide = data.pick;
  const winner = winnerSide === 'B' ? data.fixture.riderB : data.fixture.riderA;
  const loser = winnerSide === 'B' ? data.fixture.riderA : data.fixture.riderB;
  const winnerName = winner?.riderName ?? (winnerSide === 'B' ? data.fixture.awayTeam : data.fixture.homeTeam);
  const loserName = loser?.riderName ?? (winnerSide === 'B' ? data.fixture.homeTeam : data.fixture.awayTeam);

  return (
    <Reorder.Item
      value={id}
      drag={!disabled}
      dragListener={false}
      dragControls={controls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      dragElastic={0}
      transition={SPRING_REORDER}
      whileDrag={disabled ? undefined : { scale: 1.015, zIndex: 10 }}
      className="relative flex min-h-[64px] items-center gap-[11px] border-t border-black/[0.05] py-3 pr-2.5 first:border-t-0"
    >
      <RiderAvatar name={winnerName} nationality={winner?.nationality} size="lg" />

      <div className="min-w-0 flex-1">
        {riderLine(winnerName, winner?.teamName)}
        {opponentLine(loserName, loser?.nationality, loser?.teamName)}
      </div>

      {disabled ? (
        <span className="w-[42px] shrink-0" aria-hidden />
      ) : (
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            data-no-pull
            onPointerDown={(e) => {
              e.stopPropagation();
              controls.start(e);
            }}
            aria-label="Drag to reorder"
            className="tap grid h-10 w-9 shrink-0 cursor-grab touch-none place-items-center rounded-[10px] text-ink3 active:cursor-grabbing active:bg-black/[0.05]"
          >
            <GripVertical width={18} height={18} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => onMove(rank, rank - 1)}
            disabled={rank === 0}
            aria-label="More confidence"
            className="grid h-7 w-6 place-items-center text-ink3 active:opacity-70 disabled:opacity-20"
          >
            <ChevronUp width={16} height={16} strokeWidth={2.6} />
          </button>
          <button
            type="button"
            onClick={() => onMove(rank, rank + 1)}
            disabled={rank === total - 1}
            aria-label="Less confidence"
            className="grid h-7 w-6 place-items-center text-ink3 active:opacity-70 disabled:opacity-20"
          >
            <ChevronDown width={16} height={16} strokeWidth={2.6} />
          </button>
        </div>
      )}
    </Reorder.Item>
  );
}

export const StakeLadderRung = memo(StakeLadderRungBase, (prev, next) => {
  return (
    prev.id === next.id &&
    prev.rank === next.rank &&
    prev.total === next.total &&
    prev.disabled === next.disabled &&
    prev.onMove === next.onMove &&
    prev.onDragStart === next.onDragStart &&
    prev.onDragEnd === next.onDragEnd &&
    prev.data.duelId === next.data.duelId &&
    prev.data.pick === next.data.pick &&
    prev.data.fixture === next.data.fixture
  );
});
